import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { usePageContents } from '../contexts/PageContentContext';

const Facilities: React.FC = () => {
  const { getPageContent, loading } = usePageContents();
  const content = getPageContent('facilities');

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex items-center justify-center min-h-[400px] mt-20">
          <div className="text-gray-500">로딩 중...</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Page Header with Image */}
      <div className="relative text-white py-24 mt-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&h=600&fit=crop&q=80"
            alt="Modern Education Facility"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        <div className="max-w-6xl mx-auto px-8 relative z-10">
          <p className="text-gray-200 mb-3 text-sm font-medium tracking-wider">센터소개</p>
          <h1 className="text-5xl md:text-6xl font-bold mb-4">시설안내</h1>
          <div className="w-20 h-1 bg-white/70"></div>
        </div>
      </div>

      {/* Mission Section - White Background */}
      <div className="flex-1 py-20 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 leading-relaxed">
              {content?.title_main || '창의와 체험이 공존하는'}
            </h2>
            <h2 className="text-4xl md:text-5xl font-bold text-green-600 mb-8 leading-relaxed">
              {content?.title_sub || '글로벌 교육공간'}
            </h2>
            <p className="text-xl text-gray-500 mb-6">
              {content?.title_eng || 'A Creative and Experiential Space for Global Learning'}
            </p>
            <div className="w-20 h-1 bg-green-500 mx-auto"></div>
          </div>
        </div>
      </div>

      {/* About Section - Gray Background */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            {content?.description ? (
              <div
                className="text-xl text-gray-700 leading-relaxed text-center prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: content.description }}
              />
            ) : (
              <>
                <p className="text-xl text-gray-700 leading-relaxed text-center">
                  동두천시 글로벌인재교육센터는 <span className="font-bold text-gray-900">언어, 문화, 과학, 예술이 조화를 이루는 복합형 글로벌 교육공간</span>으로, 수강생이 다양한 체험을 통해 스스로 배우고 탐구할 수 있는 창의적 학습 환경을 제공합니다.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed text-center">
                  유아부터 성인까지 모든 세대가 편안하고 안전하게 학습할 수 있도록 <span className="font-semibold text-green-600">연령과 프로그램 특성에 맞춘 전용 교육실과 체험존</span>을 갖추었으며, 최신 ICT 기술을 기반으로 한 디지털 학습 시스템을 구축하여 효율적이고 몰입도 높은 교육이 이루어지도록 하였습니다.
                </p>
              </>
            )}

            {/* 이미지가 있으면 표시 */}
            {content?.image_url && (
              <div className="mt-12">
                <img
                  src={content.image_url}
                  alt="시설안내 이미지"
                  className="w-full max-w-4xl mx-auto rounded-2xl shadow-lg"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Facility Features Section - White Background */}
      <div className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-3xl">🏢</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              시설 특징
            </h2>
            <p className="text-gray-600">Facility Features</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 01 */}
            <div className="group">
              <div className="bg-gradient-to-br from-green-50 to-white border border-gray-200 rounded-2xl p-8 h-full hover:shadow-xl transition-shadow duration-300">
                <div className="text-5xl mb-6 text-center">🎨</div>
                <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
                  복합형 교육공간
                </h3>
                <p className="text-gray-600 leading-relaxed text-center">
                  언어·문화·과학·예술이<br />
                  조화를 이루는<br />
                  통합 학습 환경
                </p>
              </div>
            </div>

            {/* Feature 02 */}
            <div className="group">
              <div className="bg-gradient-to-br from-teal-50 to-white border border-gray-200 rounded-2xl p-8 h-full hover:shadow-xl transition-shadow duration-300">
                <div className="text-5xl mb-6 text-center">👥</div>
                <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
                  연령별 맞춤 공간
                </h3>
                <p className="text-gray-600 leading-relaxed text-center">
                  유아부터 성인까지<br />
                  각 연령대에 최적화된<br />
                  전용 교육실
                </p>
              </div>
            </div>

            {/* Feature 03 */}
            <div className="group">
              <div className="bg-gradient-to-br from-blue-50 to-white border border-gray-200 rounded-2xl p-8 h-full hover:shadow-xl transition-shadow duration-300">
                <div className="text-5xl mb-6 text-center">💻</div>
                <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
                  디지털 학습 시스템
                </h3>
                <p className="text-gray-600 leading-relaxed text-center">
                  최신 ICT 기술 기반<br />
                  효율적이고 몰입도 높은<br />
                  스마트 교육 환경
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Facilities Section - Gray Background */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-3xl">🏛️</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              주요 시설
            </h2>
            <p className="text-gray-600">Main Facilities</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Facility Card 01 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-xl transition-shadow duration-300">
              <div className="h-48 bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center">
                <span className="text-6xl">📚</span>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">미래지식탐색관(강의실)</h3>
                <p className="text-gray-600 leading-relaxed">
                  유아, 초등, 중·고등, 성인 각 연령대별 특성에 맞춘 전용 강의실로 최적의 학습 환경을 제공합니다.
                </p>
              </div>
            </div>

            {/* Facility Card 02 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-xl transition-shadow duration-300">
              <div className="h-48 bg-gradient-to-br from-teal-100 to-teal-50 flex items-center justify-center">
                <span className="text-6xl">🌐</span>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">세계음식관(쿠킹룸)</h3>
                <p className="text-gray-600 leading-relaxed">
                  세계 각국의 문화를 체험하고 이해할 수 있는 몰입형 문화 체험 공간입니다.
                </p>
              </div>
            </div>

            {/* Facility Card 03 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-xl transition-shadow duration-300">
              <div className="h-48 bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center">
                <span className="text-6xl">🔬</span>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">STEAM 체험관</h3>
                <p className="text-gray-600 leading-relaxed">
                  과학, 기술, 공학, 예술, 수학을 융합한 창의적 체험학습 및 VR체험이 가능한 특화 교실입니다.
                </p>
              </div>
            </div>

            {/* Facility Card 04 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-xl transition-shadow duration-300">
              <div className="h-48 bg-gradient-to-br from-purple-100 to-purple-50 flex items-center justify-center">
                <span className="text-6xl">🎭</span>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">국제관(강당)</h3>
                <p className="text-gray-600 leading-relaxed">
                  공연, 발표, 세미나 등 다양한 활동이 가능한 유연한 복합 공간입니다.
                </p>
              </div>
            </div>

            {/* Facility Card 05 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-xl transition-shadow duration-300">
              <div className="h-48 bg-gradient-to-br from-rose-100 to-rose-50 flex items-center justify-center">
                <span className="text-6xl">🖥️</span>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">세계탐색관(미디어교실)</h3>
                <p className="text-gray-600 leading-relaxed">
                  최신 ICT 장비를 갖춘 온라인·하이브리드 학습 전용 공간입니다.
                </p>
              </div>
            </div>

            {/* Facility Card 06 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-xl transition-shadow duration-300">
              <div className="h-48 bg-gradient-to-br from-amber-100 to-amber-50 flex items-center justify-center">
                <span className="text-6xl">💡</span>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">스마트 라운지</h3>
                <p className="text-gray-600 leading-relaxed">
                  자유로운 학습과 교류가 가능한 개방형 휴게·소통 공간입니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vision Section - Green Background with Image */}
      <div className="py-20 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1920&h=600&fit=crop&q=80"
            alt="Modern Learning Space"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-green-700/90 to-teal-800/90"></div>
        </div>

        <div className="max-w-6xl mx-auto px-8 relative z-10">
          <div className="text-center">
            <div className="inline-block mb-6">
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <span className="text-2xl">✨</span>
                <span className="font-semibold text-lg">Our Space</span>
              </div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-relaxed">
              누구나 편안하게 배우고<br />
              함께 성장하는<br />
              열린 배움의 공간
            </h2>
            <p className="text-xl text-green-100 max-w-3xl mx-auto leading-relaxed">
              안전하고 쾌적한 환경에서<br />
              창의적 학습 경험을 제공합니다
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Facilities;
